import React from 'react'
import PropTypes from 'prop-types'
import useKpiMeasures from '../../hooks/useKpiMeasures'
import Badge from '../ui/Badge'
import Banner from '../ui/Banner'
import KpiCard from '../ui/KpiCard'
import KpiCardRow from '../ui/KpiCard/KpiCardRow'
import BellOnIcon from '../ui/KpiCard/icons/BellOnIcon'
import FlagIcon from '../ui/KpiCard/icons/FlagIcon'
import { alertRateStatus, alertStatus, formatInteger, formatPercent } from '../ui/KpiCard/status'

const ERROR_MESSAGE = 'Impossible de charger les indicateurs.'

const CONTROLS_TOOLTIP = 'Ressources pour lesquelles 100 % des sections sont remplies.'
const SPONTANEOUS_CONTROLS_TOOLTIP = 'Réponses pour lesquelles 100 % des sections sont remplies.'
const ADDITIONAL_CONTROLS_TOOLTIP = ' Les contrôles supplémentaires sont inclus.'
const ALERTS_TOOLTIP = 'Nombre d\'alertes déclenchées automatiquement lors d\'une'
  + ' anomalie détectée dans une des collectes.'
const ALERT_RATE_TOOLTIP = 'Part des contrôles terminés ayant déclenché au moins une alerte.'

// Grade and satisfaction are not shown here, so they are not requested.
const MEASURES = ['controls', 'additional_controls', 'expected_controls', 'alerts', 'alerts_rate']
const SPONTANEOUS_MEASURES = ['controls', 'additional_controls', 'alerts', 'alerts_rate']

// Additional controls are unplanned, so they count on both ends of the ratio:
// Each one is carried out by definition, and expected since it happened. The
// Planifications page leaves them out, so its percentage can differ.
const expectedTotal = (expectedControls, additionalControls) => {
  if (!Number.isFinite(expectedControls))
    return null
  return expectedControls + (additionalControls || 0)
}

const progressBadge = (controlsCount, expectedControls) => {
  if (!Number.isFinite(controlsCount) || !expectedControls)
    return null
  return <Badge variant="neutral">{formatPercent(controlsCount / expectedControls * 100)}</Badge>
}

const KpiCardsRow = ({
  className, collectId, draftId, dateRange, hasAdditionalAnswer, hasAlertsConfigured,
  operatorId, resourceSearch, spontaneous
}) => {
  const { values, error } = useKpiMeasures(collectId, draftId, {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    measures: spontaneous ? SPONTANEOUS_MEASURES : MEASURES,
    operatorId,
    resourceSearch
  })

  if (error)
    return <Banner variant="danger">{ERROR_MESSAGE}</Banner>

  const {
    additionalControlsCount, alertsCount, alertsRate, controlsCount, expectedControlsCount
  } = values
  const additionalControlsNote = hasAdditionalAnswer ? ADDITIONAL_CONTROLS_TOOLTIP : ''
  const expectedControls = expectedTotal(expectedControlsCount, additionalControlsCount)

  return (
    <KpiCardRow className={className}>
      <KpiCard
        badge={progressBadge(controlsCount, expectedControls)}
        icon={<FlagIcon />}
        label="Nombre de contrôles"
        suffix={expectedControls ? `/${formatInteger(expectedControls)}` : null}
        tooltip={(spontaneous ? SPONTANEOUS_CONTROLS_TOOLTIP : CONTROLS_TOOLTIP) + additionalControlsNote}
        value={formatInteger(controlsCount)}
      />
      {hasAlertsConfigured ? (
        <KpiCard
          icon={<BellOnIcon />}
          label="Alertes déclenchées"
          status={alertStatus(alertsCount || 0)}
          tooltip={ALERTS_TOOLTIP}
          value={formatInteger(alertsCount)}
        />
      ) : null}
      {hasAlertsConfigured ? (
        <KpiCard
          icon={<BellOnIcon />}
          label="Taux d'alerte"
          status={alertRateStatus(alertsRate)}
          tooltip={ALERT_RATE_TOOLTIP}
          value={formatPercent(alertsRate)}
        />
      ) : null}
    </KpiCardRow>
  )
}

KpiCardsRow.propTypes = {
  className: PropTypes.string,
  collectId: PropTypes.number.isRequired,
  dateRange: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string
  }).isRequired,
  draftId: PropTypes.number,
  hasAdditionalAnswer: PropTypes.bool,
  hasAlertsConfigured: PropTypes.bool,
  operatorId: PropTypes.string,
  resourceSearch: PropTypes.string,
  spontaneous: PropTypes.bool
}

KpiCardsRow.defaultProps = {
  className: '',
  draftId: null,
  hasAdditionalAnswer: false,
  hasAlertsConfigured: false,
  operatorId: '',
  resourceSearch: '',
  spontaneous: false
}

export default KpiCardsRow
